import { useMemo, useState } from 'react';
import { useActivityPage, exactWhen, timeAgo, presetRange, type RangePreset, type ActivityItem } from '../lib/activity';
import { todayLocal } from '../lib/lookups';
import Shell from '../components/Shell';
import { EmptyState, TableSkeleton, ErrorState } from '../components/ui';
import {
  Activity as ActivityIcon,
  Calendar,
  Clock,
  ArrowLeft,
  ArrowRight,
  RotateCcw,
  CheckCircle2,
  XCircle,
  PlusCircle,
  Edit3,
  Trash2,
  Undo2,
  Wallet,
  Truck,
  HardHat,
  Box,
  Building2,
  Users,
  FileText,
  Repeat,
  Shield,
  Sliders,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';

const PAGE_SIZE = 50;

const PRESETS: Array<{ key: RangePreset; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: '24h', label: '24 Hours' },
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
  { key: 'all', label: 'All History' },
];

export default function Activity() {
  const [before, setBefore] = useState<number | null>(null);
  const [back, setBack] = useState<number[]>([]);
  const [preset, setPreset] = useState<RangePreset>('all');
  const [customFrom, setCustomFrom] = useState(todayLocal());
  const [customTo, setCustomTo] = useState(todayLocal());

  // Memoised: 24H/7D/30D anchor to Date.now(), and recomputing that on every
  // render would change the query key each time and refetch in a loop.
  const range = useMemo(
    () => presetRange(preset, preset === 'custom' ? { from: customFrom, to: customTo } : undefined),
    [preset, customFrom, customTo],
  );

  const { data, isLoading, isFetching, error, refetch } = useActivityPage(before, PAGE_SIZE, range);
  const items = data?.items ?? [];
  const page = back.length + 1;

  function goNext() {
    if (!data?.nextCursor) return;
    setBack((b) => [...b, before ?? -1]); // -1 marks "page 1", the null cursor
    setBefore(data.nextCursor);
  }

  function goPrev() {
    const prior = back.at(-1);
    if (prior === undefined) return;
    setBack((b) => b.slice(0, -1));
    setBefore(prior === -1 ? null : prior);
  }

  // A new range is a new result set — always start it back at page 1.
  function choosePreset(next: RangePreset) {
    setPreset(next);
    setBefore(null);
    setBack([]);
  }

  function editCustom(which: 'from' | 'to', v: string) {
    if (which === 'from') setCustomFrom(v);
    else setCustomTo(v);
    setPreset('custom');
    setBefore(null);
    setBack([]);
  }

  return (
    <Shell title="Live Activity Feed" crumb="Audit Trail">
      <div className="space-y-4">
        {/* ======================= Filter Bar & Controls ======================= */}
        <div className="bg-white/90 backdrop-blur-md rounded-[10px] border border-slate-200/80 p-3.5 sm:p-4 shadow-sm space-y-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            {/* Range Preset Tabs */}
            <div className="flex items-center gap-1.5 p-1 bg-slate-100/80 rounded-[8px] border border-slate-200/70 overflow-x-auto max-w-full">
              {PRESETS.map((p) => {
                const active = preset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => choosePreset(p.key)}
                    className={`px-3 py-1.5 rounded-[6px] text-xs font-semibold whitespace-nowrap transition-all ${
                      active
                        ? 'bg-white text-sky-700 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}

              <button
                type="button"
                onClick={() => choosePreset('custom')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[6px] text-xs font-semibold whitespace-nowrap transition-all ${
                  preset === 'custom'
                    ? 'bg-white text-sky-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                }`}
              >
                <Calendar className="w-3.5 h-3.5 text-sky-600" />
                Custom Range
              </button>
            </div>

            {/* Quick Refresh */}
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <button
                type="button"
                onClick={() => void refetch()}
                disabled={isFetching}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200/80 transition-all disabled:opacity-50"
              >
                <RotateCcw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin text-sky-600' : ''}`} />
                {isFetching ? 'Refreshing…' : 'Refresh Feed'}
              </button>
            </div>
          </div>

          {/* Custom Date Range Picker */}
          {preset === 'custom' && (
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-600">From:</span>
                <input
                  type="date"
                  value={customFrom}
                  max={customTo}
                  onChange={(e) => editCustom('from', e.target.value)}
                  className="px-2.5 py-1 text-xs bg-slate-50 border border-slate-200/80 rounded-[6px] text-slate-800 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-600">To:</span>
                <input
                  type="date"
                  value={customTo}
                  min={customFrom}
                  max={todayLocal()}
                  onChange={(e) => editCustom('to', e.target.value)}
                  className="px-2.5 py-1 text-xs bg-slate-50 border border-slate-200/80 rounded-[6px] text-slate-800 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
              </div>

              <span className="text-xs text-slate-500 italic">
                Showing logs strictly between selected dates (inclusive)
              </span>
            </div>
          )}
        </div>

        {/* ======================= Activity Stream Card ======================= */}
        <div className="bg-white/90 backdrop-blur-md rounded-[10px] border border-slate-200/80 shadow-sm overflow-hidden">
          {/* Card Header */}
          <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div>
              <h3 className="text-sm sm:text-base font-bold text-slate-800 m-0 flex items-center gap-2">
                <ActivityIcon className="w-4 h-4 text-sky-600" />
                Enterprise Audit Trail
              </h3>
              <p className="text-xs text-slate-500 m-0 mt-0.5">
                {preset === 'all'
                  ? 'Complete chronicle of all transactions, approvals, stock movements, and updates.'
                  : rangeLabel(preset, customFrom, customTo)}
              </p>
            </div>

            {!isLoading && !error && items.length > 0 && (
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200/80">
                {items.length} events on page {page}
              </span>
            )}
          </div>

          {/* Loading / Error / Empty States */}
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={8} cols={2} />
            </div>
          ) : error ? (
            <div className="p-6">
              <ErrorState error={error} onRetry={() => void refetch()} />
            </div>
          ) : !items.length ? (
            <div className="p-10">
              <EmptyState
                icon="clipboard"
                title={preset === 'all' ? 'No activity records found' : 'No activity in this time range'}
                hint={
                  preset === 'all'
                    ? 'Entries, approvals, and financial logs will automatically appear here as they occur.'
                    : 'Try selecting a broader date range or switch to All History.'
                }
              />
            </div>
          ) : (
            /* ======================= Timeline Event Rows ======================= */
            <div className="divide-y divide-slate-100">
              {items.map((a) => {
                const toneStyle = getToneStyle(a.tone);
                const IconComp = getSubjectIcon(a.icon, a.tone);

                return (
                  <div
                    key={a.id}
                    className="p-3.5 sm:p-4 hover:bg-slate-50/60 transition-colors flex items-start gap-3.5"
                  >
                    {/* Event Tone Icon */}
                    <div
                      className={`w-10 h-10 rounded-[8px] flex items-center justify-center shrink-0 shadow-xs border ${toneStyle.bg} ${toneStyle.border} ${toneStyle.text}`}
                      style={{ width: 40, height: 40 }}
                    >
                      <IconComp className="w-5 h-5" style={{ width: 20, height: 20 }} />
                    </div>

                    {/* Event Content Details */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1">
                        <div className="text-xs sm:text-sm text-slate-800 font-medium leading-snug">
                          {renderHighlightedText(a.text, a.who)}
                        </div>

                        {/* Amount Badge */}
                        {a.amountText && (
                          <div className="font-mono font-bold text-xs sm:text-sm text-slate-900 tabular-nums shrink-0 sm:text-right">
                            {a.amountText}
                          </div>
                        )}
                      </div>

                      {/* Subtitle / Project Context & Timestamp */}
                      <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-slate-500">
                        {a.projectName && (
                          <span className="inline-flex items-center gap-1 font-semibold text-sky-700 bg-sky-50 px-2 py-0.5 rounded-[4px] border border-sky-200/60 text-[11px]">
                            <Building2 className="w-3 h-3 text-sky-500" />
                            {a.projectName}
                          </span>
                        )}

                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400" title={timeAgo(a.at)}>
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span>{exactWhen(a.at)}</span>
                          <span className="text-slate-300">•</span>
                          <span className="text-slate-500 font-medium">{timeAgo(a.at)}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ======================= Bottom Keyset Pagination ======================= */}
          {!isLoading && !error && (back.length > 0 || data?.nextCursor) && (
            <div className="p-3.5 sm:p-4 bg-slate-50/70 border-t border-slate-200/80 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={goPrev}
                disabled={!back.length}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-[6px] bg-white border border-slate-200/80 text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-xs"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Newer (Previous)
              </button>

              <span className="text-xs font-bold text-slate-700 bg-white px-3 py-1 rounded-[6px] border border-slate-200/80 shadow-xs">
                Page {page}
              </span>

              <button
                type="button"
                onClick={goNext}
                disabled={!data?.nextCursor}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-[6px] bg-white border border-slate-200/80 text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-xs"
              >
                Older (Next)
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function rangeLabel(preset: RangePreset, from: string, to: string): string {
  const fmt = (d: string) =>
    new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  switch (preset) {
    case 'today':
      return `Showing logs for today (${fmt(todayLocal())})`;
    case '24h':
      return 'Showing audit trail for the last 24 rolling hours';
    case '7d':
      return 'Showing audit trail for the last 7 calendar days';
    case '30d':
      return 'Showing audit trail for the last 30 calendar days';
    case 'custom':
      return from === to ? `Showing logs for ${fmt(from)}` : `Showing logs from ${fmt(from)} to ${fmt(to)}`;
    default:
      return 'Complete chronological audit log, newest records first';
  }
}

interface ToneConfig {
  bg: string;
  border: string;
  text: string;
}

function getToneStyle(tone: ActivityItem['tone']): ToneConfig {
  switch (tone) {
    case 'approved':
    case 'restored':
      return { bg: 'bg-emerald-50', border: 'border-emerald-200/80', text: 'text-emerald-700' };
    case 'returned':
    case 'deleted':
      return { bg: 'bg-rose-50', border: 'border-rose-200/80', text: 'text-rose-700' };
    case 'added':
      return { bg: 'bg-sky-50', border: 'border-sky-200/80', text: 'text-sky-700' };
    case 'updated':
    default:
      return { bg: 'bg-slate-100/80', border: 'border-slate-200/80', text: 'text-slate-600' };
  }
}

function getSubjectIcon(rawIcon: string, tone: ActivityItem['tone']): LucideIcon {
  if (tone === 'approved') return CheckCircle2;
  if (tone === 'returned') return XCircle;
  if (tone === 'deleted') return Trash2;
  if (tone === 'restored') return Undo2;

  switch (rawIcon) {
    case 'wallet':
      return Wallet;
    case 'truck':
      return Truck;
    case 'hardhat':
      return HardHat;
    case 'box':
      return Box;
    case 'repeat':
      return Repeat;
    case 'transfer':
      return Repeat;
    case 'briefcase':
      return Building2;
    case 'users':
      return Users;
    case 'filetext':
      return FileText;
    case 'bank':
      return Wallet;
    case 'shield':
      return Shield;
    case 'sliders':
      return Sliders;
    default:
      return Edit3;
  }
}

function renderHighlightedText(fullText: string, actorName: string) {
  if (!fullText.startsWith(actorName)) {
    return <span>{fullText}</span>;
  }

  const rest = fullText.slice(actorName.length);
  return (
    <span>
      <strong className="font-semibold text-slate-900">{actorName}</strong>
      <span className="text-slate-700">{rest}</span>
    </span>
  );
}
